// How much of a partial transcript actually got transcribed.
//
// A free transcription stops at a fixed preview length, so a 92-minute meeting
// comes back as five minutes of text. Nothing in the transcript body says so —
// the only marker is a paywall footer below every segment, which a reader
// reaches only by scrolling past text that looks complete. These helpers feed
// the floating notice that says it up front.

const isUsableSeconds = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

/** Seconds of media the transcript actually covers: the last segment's end. */
export const getTranscribedSeconds = (
  segments?: ReadonlyArray<{ end: number }>,
): number | null => {
  if (!segments?.length) return null;
  let latest = 0;
  for (const segment of segments) {
    // Segment ends arrive from several providers; a NaN or a negative would
    // otherwise poison Math.max and take the percentage with it.
    if (isUsableSeconds(segment.end) && segment.end > latest) {
      latest = segment.end;
    }
  }
  return latest > 0 ? latest : null;
};

/**
 * The transcribed share as a whole percentage, or null when the honest answer
 * is "we don't know yet" — media duration still loading, either value
 * unusable, or the transcript already covers the file.
 */
export const getTranscribedPercent = ({
  transcribedSeconds,
  mediaSeconds,
}: {
  transcribedSeconds: number | null | undefined;
  mediaSeconds: number | null | undefined;
}): number | null => {
  if (!isUsableSeconds(transcribedSeconds)) return null;
  if (!isUsableSeconds(mediaSeconds)) return null;
  const ratio = transcribedSeconds / mediaSeconds;
  // At the rounding threshold for 100% there is nothing left to unlock. A
  // decoder that reports a duration a hair under the last segment's end must
  // not turn a complete transcript into a "99% transcribed" upsell.
  if (ratio >= 0.995) return null;
  // And never round a real gap down to nothing: a 5-minute preview of a
  // 10-hour file is 0.8%, and "0% transcribed" reads as a failed job rather
  // than a preview of a working one.
  return Math.max(1, Math.round(ratio * 100));
};

/**
 * Whole minutes of media the preview leaves out, rounded up so the offer never
 * understates what is locked. Null under the same "we don't know" rules as the
 * percentage.
 */
export const getRemainingMinutes = ({
  transcribedSeconds,
  mediaSeconds,
}: {
  transcribedSeconds: number | null | undefined;
  mediaSeconds: number | null | undefined;
}): number | null => {
  if (getTranscribedPercent({ transcribedSeconds, mediaSeconds }) === null) {
    return null;
  }
  return Math.ceil(((mediaSeconds as number) - (transcribedSeconds as number)) / 60);
};

// Failures the file itself caused. Re-uploading the same file fails the same
// way, so the failed screen names the cause instead of a generic "try again".
// Every other code (provider outages, timeouts) keeps the generic copy.
export type TranscriptFailureReason =
  | "damaged"
  | "empty"
  | "silent"
  | "noAudio"
  | "unsupported";

export const getTranscriptFailureReason = (
  transcriptionError?: string,
): TranscriptFailureReason | undefined => {
  switch (transcriptionError) {
    case "AudioDecodeFailed":
      return "damaged";
    case "AudioEmpty":
    case "AudioFileTooSmall":
      return "empty";
    case "NoAudibleAudio":
      return "silent";
    case "NoAudioTrackFound":
      return "noAudio";
    case "UnsupportedFile":
      return "unsupported";
    default:
      return undefined;
  }
};

// A completed transcript the free model made. Premium redoes it once, so the
// draft notice and the post-purchase re-run both key off this one rule (the
// server applies the same one before re-queueing).
export const isFreeDraft = (transcript?: {
  status?: string;
  transcribedWithPremium?: boolean;
}): boolean =>
  transcript?.status === "transcription_completed" &&
  transcript.transcribedWithPremium === false;
